import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    IQueryContextDatasourceColumn,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { BIVizDashboardAdEvents } from '../sql-maps/biviz-ad-events-datasource-sql-map';

export interface AdsActivityDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class AdsActivityDataLoaderFactory extends Factory<
    DataLoader,
    AdsActivityDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'AdsActivityDataLoaderFactory';

    getInstance(options: AdsActivityDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                // if queryContext contains DAILY_AD_CONSENT, then replace it with AD_CONSENT
                // this DataLoader should load event data, and event level data is collected in AD_CONSENT column instead of DAILY_AD_CONSENT
                queryContext.datasourceColumns =
                    queryContext.datasourceColumns.map(
                        (column: IQueryContextDatasourceColumn) => {
                            if (column.id === 'DAILY_AD_CONSENT') {
                                column.id = 'AD_CONSENT';
                            }
                            return column;
                        }
                    );

                const adEventsDaily = BIVizDashboardAdEvents;
                // copy the sql map to avoid modifying the original
                const adEventsDailySqlMap = adEventsDaily.sqlMap;

                // identify columns not supported in datasource
                const adEventsDailyDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        adEventsDailySqlMap.columns
                    );

                const adEventsDailyDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        adEventsDailyDsColumns.missing
                    );

                const adEventsDailySQL = adEventsDaily.compileSQLStatements(
                    adEventsDailyDsColumns.present,
                    queryContext.settings,
                    options,
                    adEventsDailySqlMap
                );

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH adEventsDaily AS
(
  SELECT 
    ${adEventsDailySQL.splitGroupBy}${adEventsDailySQL.dateTrunc} AS date
    , SUM(completions) AS completions
    , SUM(impressions) AS impressions
    , SUM(ad_not_available_count) AS ad_not_available_count
    , SUM(ad_watch_clicks) AS ad_watch_clicks
    , SUM(ad_cancel_clicks) AS ad_cancel_clicks
    , SUM(ad_loading_errors) AS ad_loading_errors
    , SUM(revenue_pln) AS revenue_pln
    , SUM(revenue_usd) AS revenue_usd
  FROM
  (
    SELECT ${adEventsDailySQL.splitGroupBy}date
      , SUM(ad_rewards) AS completions
      , SUM(ad_show_starts) AS impressions
      , SUM(ad_not_available_count) AS ad_not_available_count
      , SUM(ad_watch_clicks) AS ad_watch_clicks
      , SUM(ad_cancel_clicks) AS ad_cancel_clicks
      , SUM(ad_loading_errors) AS ad_loading_errors
      , SUM(revenue_net_pln) AS revenue_pln
      , SUM(revenue_net_usd) AS revenue_usd
    FROM 
    (
      SELECT 
        ds.*,
        ${adEventsDailySQL.splitSelect}
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          adEventsDailySqlMap.dataset,
          adEventsDailySqlMap.tableId
      )}\` ds
      ${adEventsDailySQL.join}
      ${adEventsDailySQL.where}
    )
    GROUP BY ${adEventsDailySQL.splitGroupBy}date 
  )
  GROUP BY ${adEventsDailySQL.splitGroupBy}date 
)

SELECT
${
    adEventsDailySQL.splitGroupBy !== ''
        ? `adEventsDaily.split_group AS split_group, `
        : ''
}adEventsDaily.date AS date
  -- Counts the total number of ad impressions served to users
  , adEventsDaily.impressions
  , ROUND(SAFE_DIVIDE(adEventsDaily.completions, adEventsDaily.impressions), 4) AS completion_rate
  -- The 'app_fill_rate' calculates the proportion of ad requests that resulted in an ad being successfully served, adjusted for cancellations. A higher rate indicates better performance in ad delivery.
  , ROUND(
      1 - SAFE_DIVIDE(
            adEventsDaily.ad_not_available_count, -- Number of times an ad was not available when requested
            adEventsDaily.ad_watch_clicks - adEventsDaily.ad_cancel_clicks -- Net ad attempts, adjusted for cancellations
      ),
      4
    ) AS app_fill_rate
  -- Measures the proportion of ad requests that were successfully filled by the ad server. A rate closer to 1 indicates fewer instances where ads were not available to be served.
  , ROUND(SAFE_DIVIDE(adEventsDaily.ad_loading_errors, adEventsDaily.ad_watch_clicks), 4) AS errors_per_ad_watch_click
  , ROUND(adEventsDaily.revenue_pln, 2) AS revenue_pln
  , ROUND(adEventsDaily.revenue_usd, 2) AS revenue_usd
  -- eCPM
    , ROUND(SAFE_DIVIDE(adEventsDaily.revenue_pln * 1000, adEventsDaily.impressions), 2) AS ecpm_pln
    , ROUND(SAFE_DIVIDE(adEventsDaily.revenue_usd * 1000, adEventsDaily.impressions), 2) AS ecpm_usd
FROM adEventsDaily
ORDER BY ${
                    adEventsDailySQL.splitGroupBy !== ''
                        ? `adEventsDaily.split_group, `
                        : ''
                }adEventsDaily.date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: adEventsDailyDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        adEventsDailyDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
